import { Map } from 'mapbox-gl';
import type { MapRef } from 'react-map-gl/mapbox';
import BoundingBox from '../types/BoundingBox';

/**
 * When a map is rotated (non-zero bearing), the getBounds function returns points outside of the visible area,
 * despite what the mapbox docs claim (https://docs.mapbox.com/mapbox-gl-js/api/map/#map#getbounds).
 * Mobile ran into the same issue here, so we are taking a similar approach and calculating our own bounding box when rotated.
 * It seems like the issue lies withing the LngLatBounds class, which defines the bounds by two opposite corners - return
 * our own 4-cornered defined BoundingBox type instead for more robust and accurate bounding info.
 * @param map Mapbox map object
 * @returns The visible bounding box of the map
 */
const getMapBoundingBox = (map: Map | MapRef): BoundingBox => {
  if (map.getBearing() === 0) {
    const bounds = map.getBounds();
    if (!bounds) {
      return {
        topLeft: { lng: 0, lat: 0 },
        topRight: { lng: 0, lat: 0 },
        bottomLeft: { lng: 0, lat: 0 },
        bottomRight: { lng: 0, lat: 0 }
      };
    }

    const boundingBox = {
      topLeft: bounds.getNorthWest(),
      topRight: bounds.getNorthEast(),
      bottomLeft: bounds.getSouthWest(),
      bottomRight: bounds.getSouthEast()
    };

    return {
      topLeft: { lng: boundingBox.topLeft.lng, lat: boundingBox.topLeft.lat },
      topRight: { lng: boundingBox.topRight.lng, lat: boundingBox.topRight.lat },
      bottomLeft: { lng: boundingBox.bottomLeft.lng, lat: boundingBox.bottomLeft.lat },
      bottomRight: { lng: boundingBox.bottomRight.lng, lat: boundingBox.bottomRight.lat }
    };
  }

  const { offsetHeight, offsetWidth } = map.getContainer();
  const padding = map.getPadding();

  const xMin = padding.left ?? 0;
  const xMax = offsetWidth - (padding.right ?? 0);
  const yMin = padding.top ?? 0;
  const yMax = offsetHeight - (padding.bottom ?? 0);

  const boundingBox = {
    topLeft: map.unproject([xMin, yMin]),
    topRight: map.unproject([xMax, yMin]),
    bottomLeft: map.unproject([xMin, yMax]),
    bottomRight: map.unproject([xMax, yMax])
  };

  return {
    topLeft: { lng: boundingBox.topLeft.lng, lat: boundingBox.topLeft.lat },
    topRight: { lng: boundingBox.topRight.lng, lat: boundingBox.topRight.lat },
    bottomLeft: { lng: boundingBox.bottomLeft.lng, lat: boundingBox.bottomLeft.lat },
    bottomRight: { lng: boundingBox.bottomRight.lng, lat: boundingBox.bottomRight.lat }
  };
};

export default getMapBoundingBox;
